import { useQueries } from '@tanstack/react-query';

import { _memberStatus } from '@/api/entity/mutations';
import { normalizeStatus } from '@/pages/setup-fund/modules/team-card';

import { useCreateEntity } from '../use-create-entity';

type MemberStatusRes = {
  status?: string;
  email?: string;
  name?: string;
  alias?: string;
  headshotImageUrl?: string;
};

export type DirectorStatus = ReturnType<typeof normalizeStatus>;

export type DirectorRow = {
  key: string;
  id: string;
  userId?: string;
  name: string;
  email: string;
  headshotImageUrl?: string;
  status: DirectorStatus;
  isMember: boolean;
  changed?: boolean;
};

export const useDirectorRows = (entityId: string) => {
  const { filteredDirectors } = useCreateEntity();

  const results = useQueries({
    queries: filteredDirectors.map((director) => {
      const email = 'email' in director ? director.email : '';
      const userId = 'userId' in director ? (director.userId ?? '') : '';

      return {
        queryKey: ['entity-member-status', entityId, email, userId],
        queryFn: (): Promise<MemberStatusRes> => _memberStatus({ id: entityId, email, userId }),
        enabled: Boolean(entityId),
      };
    }),
  });

  const rows: DirectorRow[] = filteredDirectors.map((director, index) => {
    const data = results[index]?.data;
    const name =
      data?.name && data?.alias
        ? `${data.name} (${data.alias})`
        : (data?.name ?? data?.alias ?? '');

    return {
      key: director.id,
      id: director.id,
      userId: 'userId' in director ? (director.userId ?? undefined) : undefined,
      name,
      email: data?.email ?? ('email' in director ? director.email : ''),
      headshotImageUrl: data?.headshotImageUrl,
      status: normalizeStatus(data?.status),
      isMember: !('email' in director),
    };
  });

  return { rows, isLoading: results.some((result) => result.isLoading) };
};
